/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import {
  Component,
  Element,
  h,
  Host,
  Listen,
  Method,
  Prop,
} from "@stencil/core"
import { closest, translate } from "../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-trap-focus",
  styleUrl: "trap-focus.scss",
  shadow: true,
})
export class TrapFocus {
  private startOfDialogHiddenEl?: HTMLDivElement
  private endOfDialogHiddenEl?: HTMLDivElement
  private slotEl?: HTMLSlotElement

  @Element() public el!: HTMLSdxTrapFocusElement

  /**
   * This element is focused if the end of focus circuit is reached and tab key
   * is pressed.
   * The element could only be a <sdx-button>.
   */
  @Prop() public firstFocusableElement?: HTMLSdxButtonElement

  @Listen("keyup") // to avoid that user has to tab two times at the last element (analog at the first element with pressed shift key)
  @Listen("keydown") // at the last element keyup event is too late (analog at the first element with pressed shift key)
  public onKey(e: KeyboardEvent) {
    const isTabPressed = e.key === "Tab"

    if (!isTabPressed) {
      return
    }

    const { startOfDialogHiddenEl, endOfDialogHiddenEl } = this
    const hasFocusElement = this.el.shadowRoot?.activeElement

    if (e.shiftKey) {
      // if shift key pressed for shift + tab combination
      if (hasFocusElement === startOfDialogHiddenEl) {
        endOfDialogHiddenEl?.focus()
        e.preventDefault()
      }
    } else {
      // if only tab key is pressed
      if (hasFocusElement === endOfDialogHiddenEl) {
        this.setFocusToFirstElement()
        e.preventDefault()
      }
    }
  }

  /**
   * Sets the focus on the component if a child does not have the focus yet.
   */
  @Method()
  public async doFocus() {
    const { el, slotEl } = this

    const elWithoutSlotContainsActiveElement = el.contains(
      document.activeElement
    )

    const slotContainsActiveElement = slotEl
      ?.assignedElements()
      .some((value) => closest(document.activeElement, value))

    if (!(slotContainsActiveElement || elWithoutSlotContainsActiveElement)) {
      this.setFocusToFirstElement()
    }
  }

  private setFocusToFirstElement() {
    const { startOfDialogHiddenEl, firstFocusableElement } = this
    firstFocusableElement
      ? firstFocusableElement?.doFocus()
      : startOfDialogHiddenEl?.focus()
  }

  public render() {
    return (
      <Host>
        <div
          class="sr-only"
          data-nosnippet
          ref={(el) => (this.startOfDialogHiddenEl = el)}
          tabindex={0}
        >
          {translate("Start of dialog", this.el.lang)}
        </div>

        <slot ref={(el) => (this.slotEl = el as HTMLSlotElement)} />

        <div
          class="sr-only"
          data-nosnippet
          ref={(el) => (this.endOfDialogHiddenEl = el)}
          tabindex={0}
        >
          {translate("End of dialog", this.el.lang)}
        </div>
      </Host>
    )
  }
}
